// AuthContext / useAuth — gives the current Supabase user to the whole app.
//
// Additive: when Supabase env vars are missing, `isConfigured` is false and
// every action returns a friendly error, so the rest of the app keeps working
// exactly as before (all modules remain usable without login).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { validateCredentials, friendlyAuthError } from '../lib/authLogic';

const NOT_CONFIGURED_ERROR = 'Accounts are not available right now.';

const AuthContext = createContext({
  user: null,
  loading: false,
  isConfigured: false,
  signUp: async () => ({ error: NOT_CONFIGURED_ERROR }),
  signIn: async () => ({ error: NOT_CONFIGURED_ERROR }),
  signOut: async () => ({ error: null }),
});

export function AuthProvider({ children, client }) {
  // `client` prop is for tests; the app uses the real supabase client.
  const auth = client !== undefined ? client : supabase;
  const configured = client !== undefined ? Boolean(client) : isSupabaseConfigured;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(configured);

  useEffect(() => {
    if (!auth) return undefined;
    let cancelled = false;

    auth.auth
      .getSession()
      .then(({ data }) => {
        if (!cancelled) {
          setUser(data?.session?.user ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    const { data: subscription } = auth.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      subscription?.subscription?.unsubscribe();
    };
  }, [auth]);

  const signUp = useCallback(
    async (email, password) => {
      if (!auth) return { error: NOT_CONFIGURED_ERROR };
      const invalid = validateCredentials(email, password, { requireStrongPassword: true });
      if (invalid) return { error: invalid };
      const { error } = await auth.auth.signUp({ email: email.trim(), password });
      return { error: friendlyAuthError(error) };
    },
    [auth]
  );

  const signIn = useCallback(
    async (email, password) => {
      if (!auth) return { error: NOT_CONFIGURED_ERROR };
      const invalid = validateCredentials(email, password);
      if (invalid) return { error: invalid };
      const { error } = await auth.auth.signInWithPassword({ email: email.trim(), password });
      return { error: friendlyAuthError(error) };
    },
    [auth]
  );

  const signOut = useCallback(async () => {
    if (!auth) return { error: null };
    const { error } = await auth.auth.signOut();
    return { error: friendlyAuthError(error) };
  }, [auth]);

  const value = useMemo(
    () => ({ user, loading, isConfigured: configured, signUp, signIn, signOut }),
    [user, loading, configured, signUp, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
